import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import config from "../../config.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import TitleBar from "../navbar/TitleBar";
import NavMenu from "../navbar/NavMenu";
import SchoolBusNavMenu from "../createForm/SchoolBusNavMenu";
import RejectModal from "./RejectModal";
import { useUser } from "../../pages/login/UserContextPersistent";
import Footer from "../common/Footer.jsx";
import {
  MANDATORY_FEATURE_CATEGORIES,
  getFeaturesForCategory,
  getOrderedFeatureCategories,
  includesSchoolTracking,
} from "../../utils/featureCategories";
import FeatureCategoryGrid from "../features/FeatureCategoryGrid";
import { ASSIGNABLE_SETTINGS_TABS } from "../../config/settingsModuleCatalog";
import { useTranslation } from "react-i18next";
// Your existing isRouteEyeAdmin function
export const isRouteEyeAdmin = () => {
  const userState = localStorage.getItem("userState");
  if (!userState) return false;
  try {
    const user = JSON.parse(userState).user;
    return user.organizationId === 5 && user.email === "sales@routeeye.io";
  } catch {
    return false;
  }
};

export default function AdminOrgAssign() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state: userState } = useUser();
  const location = useLocation();
  console.log(location.state);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [isRequestView, setIsRequestView] = useState(false);
  const [allFeatures, setAllFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [enabledSettingsTabs, setEnabledSettingsTabs] = useState(
    ASSIGNABLE_SETTINGS_TABS.map((tab) => tab.tabKey),
  );
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [orgNameToReject, setOrgNameToReject] = useState(
    location.state?.selectedOrganization?.name || "",
  );

  const [message, setMessage] = useState("");

  const requestId = location.state?.requestId || null;
  const defaultCategories = MANDATORY_FEATURE_CATEGORIES;
  const isSchoolTrackingSelected = includesSchoolTracking(
    organizationDetails?.organizationType,
    location.state?.selectedOrganization?.organizationType,
  );

  const isAdmin = isRouteEyeAdmin();

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000); // Message disappears after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const path = location.pathname;
    const isReq = path.includes("/request/");
    const isReqFeature = path.includes("/request/feature");
    setIsRequestView(isReq);

    if (location.state && location.state.selectedOrganization) {
      const orgData = location.state.selectedOrganization;
      setOrganizationDetails(orgData);
      if (orgData.approvalStatus) setApprovalStatus(orgData.approvalStatus);

      // if (isReqFeature && orgData.requestedFeatures?.length > 0) {
      //   const initSelected = {};
      //   orgData.requestedFeatures.forEach((f) => {
      //     initSelected[f.featureId] = true;
      //   });
      //   setSelectedFeatures(initSelected);
      // }
    } else if (id) {
      fetchOrganizationDetails(id);
    }

    getAllTheAvailableFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location]);

  useEffect(() => {
    const isReqFeature = location.pathname.includes("/request/feature");
    const orgData = location.state?.selectedOrganization;

    if (
      isReqFeature &&
      orgData?.requestedFeatures?.length > 0 &&
      allFeatures.length > 0
    ) {
      const initSelected = {};
      orgData.requestedFeatures.forEach((f) => {
        initSelected[f.featureId] = true;
      });
      setSelectedFeatures(initSelected);
    }
  }, [allFeatures, location.state, location.pathname]);

  const fetchOrganizationDetails = async (orgId) => {
    try {
      const res = await axios.get(`${config.baseURL}/organization/${orgId}`, {
        withCredentials: true,
      });
      const data = res.data.organization || res.data;
      console.log(data);
      setOrganizationDetails(data);
      if (data.approvalStatus) setApprovalStatus(data.approvalStatus);
    } catch (err) {
      console.error("Error fetching organization details:", err);
      setError(t("Failed to load organization details"));
    }
  };

  const getAllTheAvailableFeatures = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.baseURL}/feature/`, {
        withCredentials: true,
      });
      setAllFeatures(res.data);
    } catch (err) {
      console.error("Error fetching features:", err);
      setError(t("Failed to load features"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allFeatures.length === 0) return;

    const finalCats = getOrderedFeatureCategories(allFeatures, {
      includeSchoolCategories: isSchoolTrackingSelected,
    });
    setCategories(finalCats);

    if (
      id &&
      !(
        location.pathname.includes("/request/feature") &&
        location.state?.selectedOrganization?.requestedFeatures?.length > 0
      )
    ) {
      fetchOrganizationFeatures(id, isRequestView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFeatures, id, location, approvalStatus, isSchoolTrackingSelected]);

  // const fetchOrganizationFeatures = async (organizationId) => {
  //   try {
  //     const res = await axios.get(
  //       `${config.baseURL}/organization/features/${organizationId}`,
  //       { withCredentials: true }
  //     );
  //     const existingFeat = res.data.features || [];
  //     const sel = {};
  //     existingFeat.forEach(({ feature }) => {
  //       sel[feature.id] = true;
  //     });
  //     setSelectedFeatures(sel);
  //   } catch (err) {
  //     console.error("Error fetching organization features:", err);
  //     setError("Failed to fetch features");
  //   }
  // };

  const fetchOrganizationFeatures = async (
    organizationId,
    isRequest = isRequestView,
  ) => {
    try {
      // const res = await axios.get(
      //   `${config.baseURL}/organization/features/${organizationId}`,
      //   { withCredentials: true }
      // );

      // Determine which endpoint to use based on whether it's a request view
      console.log("Is Request View:", isRequest);
      const endpoint = isRequest
        ? `${config.baseURL}/organization/req-features/${requestId}`
        : `${config.baseURL}/organization/features/${organizationId}`;

      console.log("Fetching features from endpoint:", endpoint);
      const res = await axios.get(endpoint, { withCredentials: true });

      const existingFeat = res.data.features || [];
      const sel = {};

      existingFeat.forEach(({ feature }) => {
        sel[feature.id] = true;
      });
      setSelectedFeatures(sel);
      setEnabledSettingsTabs(
        Array.isArray(res.data.enabledSettingsTabs)
          ? res.data.enabledSettingsTabs
          : ASSIGNABLE_SETTINGS_TABS.map((tab) => tab.tabKey),
      );
    } catch (err) {
      console.error("Error fetching organization features:", err);
      // Get the error message from the API response
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.msg ||
        err.response?.data?.message;

      if (errorMessage) {
        setError(errorMessage);
      } else if (err.response?.status === 403 || err.response?.status === 401) {
        setError("pending_approval");
      } else {
        setError(t("Failed to fetch features"));
      }
    }
  };

  const isAddOnFeature = (feature) => feature.isDefault === false;

  const getFeaturesByCategory = (category) =>
    getFeaturesForCategory(allFeatures, category);

  const getCategoryCheckboxState = (category) => {
    const regularFeatures = getFeaturesByCategory(category).filter(
      (f) => !isAddOnFeature(f),
    );
    const selCount = regularFeatures.filter(
      (f) => selectedFeatures[f.id],
    ).length;
    const allSel =
      regularFeatures.length > 0 && selCount === regularFeatures.length;
    const someSel = selCount > 0 && !allSel;
    return { checked: allSel, indeterminate: someSel };
  };

  const handleCategoryChange = (category) => {
    if (defaultCategories.includes(category)) return;
    const regularFeatures = getFeaturesByCategory(category).filter(
      (f) => !isAddOnFeature(f),
    );
    const allSelected = regularFeatures.every((f) => selectedFeatures[f.id]);
    setSelectedFeatures((prev) => {
      const updated = { ...prev };
      regularFeatures.forEach((f) => {
        updated[f.id] = !allSelected;
      });
      return updated;
    });
  };

  const handleAddOnChange = (featureId) => {
    setSelectedFeatures((prev) => ({ ...prev, [featureId]: !prev[featureId] }));
  };

  const handleSettingsTabToggle = (tabKey) => {
    setEnabledSettingsTabs((currentTabs) =>
      currentTabs.includes(tabKey)
        ? currentTabs.filter((key) => key !== tabKey)
        : [...currentTabs, tabKey],
    );
  };

  const getSelectedFeatureIds = () => {
    const selectedIds = new Set();

    defaultCategories.forEach((category) => {
      getFeaturesByCategory(category)
        .filter((feature) => !isAddOnFeature(feature))
        .forEach((feature) => {
          selectedIds.add(feature.id);
        });
    });

    Object.keys(selectedFeatures).forEach((id) => {
      if (selectedFeatures[id]) {
        selectedIds.add(parseInt(id, 10));
      }
    });

    return Array.from(selectedIds);
  };

  const hasPermission = (path, action) => {
    if (!userState?.features) return false;
    return userState.features.some(
      (f) => f.api_path === path && f.action === action,
    );
  };

  const mapDetailsFeature = allFeatures.find((feature) => {
    const apiPath = String(feature.api_path || "").trim();
    const normalizedPath = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
    return normalizedPath === "/api/settings/mapDetails";
  });

  const handleSave = async ({ redirect } = {}) => {
    const shouldRedirect = redirect ?? !isRequestView;
    const selectedIds = getSelectedFeatureIds();
    const orgId =
      id || location.state?.selectedOrganization?.id || organizationDetails?.id;
    if (!id) {
      setNotification({
        open: true,
        message: t("No organization ID provided"),
        severity: "error",
      });
      return false;
    }
    try {
      // await axios.put(
      //   `${config.baseURL}/organization/features`,
      //   { organizationId: Number(id), featureIds: selectedIds },
      //   { withCredentials: true }
      // );
      // setMessage("Features assigned successfully");
      if (isRequestView) {
        // For request view - use the same API as Register component
        await axios.put(
          `${config.baseURL}/register/${requestId}`,
          { requestedFeatureIds: selectedIds, enabledSettingsTabs },
          { withCredentials: true },
        );
        setMessage(t("Requested features updated successfully"));
      } else {
        // Otherwise, assign features to organization (admin flow)
        await axios.put(
          `${config.baseURL}/organization/features`,
          {
            organizationId: Number(id),
            featureIds: selectedIds,
            enabledSettingsTabs,
          },
          { withCredentials: true },
        );
        setMessage(t("Features assigned successfully"));
      }
      // setNotification({
      //   open: true,
      //   message: "Features assigned successfully",
      //   severity: "success",
      // });
      if (shouldRedirect) navigate("/org");
      return true;
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.msg || t("Failed to assign features"),
        severity: "error",
      });
      return false;
    }
  };

  const handleRequestFeatures = async () => {
    const selectedIds = getSelectedFeatureIds();
    if (selectedIds.length === 0) {
      setNotification({
        open: true,
        message: t("Please select at least one feature to request."),
        severity: "warning",
      });
      return;
    }
    if (!id) {
      setNotification({
        open: true,
        message: t("No organization ID provided"),
        severity: "error",
      });
      return;
    }
    try {
      await axios.post(
        `${config.baseURL}/organization/${id}/request-features`,
        { featureIds: selectedIds },
        { withCredentials: true },
      );
      setNotification({
        open: true,
        message: "Feature request submitted successfully!",
        severity: "success",
      });
      navigate(-1);
    } catch (error) {
      setNotification({
        open: true,
        message:
          error.response?.data?.msg || t("Failed to submit feature request"),
        severity: "error",
      });
    }
  };

  const handleCancel = () => navigate(isRequestView ? "/admin" : -1);

  const handleViewMore = () => {
    const orgData = { ...organizationDetails, approvalStatus };
    const orgId =
      organizationDetails?.organizationId || organizationDetails?.id || id;
    const path = isRequestView
      ? `/request/viewMore/${orgId}`
      : `/organization/viewMore/${orgId}`;
    navigate(path, {
      state: {
        selectedOrganization: orgData,
        requestId,
      },
    });
  };

  const handleAccept = async () => {
    setIsActionLoading(true);
    try {
      const saved = await handleSave({ redirect: false });
      if (!saved) return;
      const targetId = isRequestView
        ? requestId || location.state?.selectedOrganization?.id
        : location.state?.selectedOrganization?.id;
      await axios.post(
        `${config.baseURL}/register/${targetId}/accept`,
        {},
        { withCredentials: true },
      );
      setNotification({
        open: true,
        message: t("Request accepted and features assigned successfully"),
        severity: "success",
      });
      navigate("/admin");
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.msg || t("Failed to accept request"),
        severity: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReject = () => setRejectModalOpen(true);

  const confirmReject = async () => {
    setIsActionLoading(true);
    try {
      const targetId = isRequestView
        ? requestId || location.state?.selectedOrganization?.id
        : location.state?.selectedOrganization?.id;
      await axios.post(
        `${config.baseURL}/register/${targetId}/reject`,
        { reason: rejectionReason },
        { withCredentials: true },
      );
      setRejectModalOpen(false);
      setNotification({
        open: true,
        message: t("Organization rejected successfully"),
        severity: "success",
      });
      navigate("/admin");
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.msg || t("Failed to reject organization"),
        severity: "error",
      });
      setRejectModalOpen(false);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCloseNotification = () =>
    setNotification((prev) => ({ ...prev, open: false }));

  const renderActionButtons = () => {
    if (isRequestView) {
      if (approvalStatus === "REJECTED") {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ minWidth: 100, height: 55, borderRadius: 2 }}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
              sx={{ minWidth: 100, height: 55, borderRadius: 2 }}
            >
              {t("Save")}
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={isActionLoading}
              sx={{ minWidth: 100, height: 55, borderRadius: 2 }}
            >
              {isActionLoading ? <CircularProgress size={24} /> : t("Accept")}
            </Button>
          </Box>
        );
      }
      return (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleReject}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: "55px",
              borderRadius: "8px",
              backgroundColor: "#EE3B2B",
              color: "white",
              fontSize: "20px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {isActionLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("Reject")
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              minWidth: 100,
              height: "55px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "20px",
              textTransform: "none",
              backgroundColor: "#F2F2F3",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSave}
            sx={{
              minWidth: 100,
              height: "55px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "20px",
              textTransform: "none",
              backgroundColor: "#F9E719",
              "&:hover": {
                backgroundColor: "#F9E719",
              },
            }}
          >
            {t("Save")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleAccept}
            disabled={isActionLoading}
            sx={{
              minWidth: 100,
              height: "55px",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "20px",
              textTransform: "none",
              backgroundColor: "#F9E719",
              "&:hover": {
                backgroundColor: "#F9E719",
              },
            }}
          >
            {isActionLoading ? <CircularProgress size={24} /> : t("Accept")}
          </Button>
        </Box>
      );
    }

    if (isAdmin) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              width: 240,
              height: 55,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "20px",
              fontWeight: "500",
              color: "#414958",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              width: 240,
              height: 55,
              borderRadius: 2,
              background: "#F9E719",
              color: "#1F242E",
              textTransform: "none",
              fontSize: "20px",
              fontWeight: "500",
              "&:hover": {
                background: "#F9E719",
                color: "#1F242E",
              },
            }}
          >
            {t("Save")}
          </Button>
        </Box>
      );
    }

    // Tenant buttons
    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            width: 240,
            height: 55,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "20px",
            fontWeight: "700",
            color: "#414958",
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleRequestFeatures();
          }}
          sx={{
            width: 240,
            height: 55,
            borderRadius: 2,
            background: "#F9E719",
            color: "#1F242E",
            textTransform: "none",
            fontSize: "20px",
            fontWeight: "700",
            fontFamily: "Roboto",
            "&:hover": {
              background: "#F9E719",
              color: "#111214",
            },
          }}
        >
          {t("Request Features")}
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title={`Assign Features${
            organizationDetails?.name ? ` to ${organizationDetails.name}` : ""
          }`}
          path={
            isRequestView
              ? `${orgName || "organization"} > New Request > Assign Features`
              : `${
                  orgName || "organization"
                } > Update Organization > Assign Features`
          }
          track="View More"
          onTrackClick={handleViewMore}
        />
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t("Loading features...")}</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title={`Assign Features${
            organizationDetails?.name ? ` to ${organizationDetails.name}` : ""
          }`}
          path={
            isRequestView
              ? `${orgName || "organization"} > New Request > Assign Features`
              : `${
                  orgName || "organization"
                } > Update Organization > Assign Features`
          }
          track="View More"
          trackPath={`/organization/viewMore/${id}`}
          onTrackClick={handleViewMore}
        />
        <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
          <Typography>{error}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#F2F2F3",
        padding: "0px 0px 20px 0px",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <TitleBar />
        {localStorage.getItem("selectedApp") === "schoolTrack" ? (
          <SchoolBusNavMenu />
        ) : (
          <NavMenu />
        )}
        {approvalStatus === "REJECTED" && (
          <Box
            sx={{
              backgroundColor: "#EE3B2B",
              color: "#FFFFFF",
              height: "36px",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: "24px",
            }}
          >
            {t("Application Rejected")}
          </Box>
        )}
        <SubTitle
          title={
            isRequestView
              ? `Assign Features${
                  organizationDetails?.name
                    ? ` to ${organizationDetails.name}`
                    : ""
                }`
              : !isAdmin
                ? // `My Features${
                  //     organizationDetails?.name ? ` - ${organizationDetails.name}` : ""
                  //   }`
                  t("Organization Features")
                : `Assign Features to ${organizationDetails?.name}`
          }
          path={
            isRequestView
              ? `${orgName || "organization"} > New Request > Assign Features`
              : !isAdmin
                ? `${
                    orgName || "organization"
                  } > Organization Profile > My Features`
                : `${orgName || "organization"} > Update Organization > ${
                    organizationDetails?.name
                  } > Assign Features`
          }
          track="View More"
          trackPath={`/organization/viewMore/${id}`}
          onTrackClick={handleViewMore}
        />
        <Paper
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            margin: "0 40px",
            border: "1px solid #A5DCF3",
            boxShadow: "-2px 2px 5px 0px #00000040",
          }}
        >
          <Box sx={{ padding: "20px" }}>
            <Box
              sx={{
                padding: "12px 24px",
                backgroundColor: "#f5f8fb",
                borderRadius: "8px",
                color: "rgba(24, 48, 89, 0.7)",
                fontSize: "14px",
                lineHeight: 1.6,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              {!isRequestView && (
                <>
                  {t(
                    "Your current plan includes the features listed below. To request more, review your selection and click Save.",
                  )}
                </>
              )}
            </Box>
            <FeatureCategoryGrid
              categories={categories}
              mandatoryCategories={defaultCategories}
              getCategoryFeatures={getFeaturesByCategory}
              getCategoryState={(category, regularFeatures) => {
                const { checked, indeterminate } =
                  getCategoryCheckboxState(category);

                return {
                  checked,
                  indeterminate,
                  disabled:
                    regularFeatures.length === 0 ||
                    !hasPermission("/api/user/organization", "PATCH"),
                };
              }}
              onCategoryToggle={(category) => handleCategoryChange(category)}
              isFeatureSelected={(_, feature) => selectedFeatures[feature.id]}
              onAddOnToggle={(_, feature) => handleAddOnChange(feature.id)}
              isAddOnDisabled={() =>
                !hasPermission("/api/user/organization", "PATCH")
              }
              headerPaddingByCategory={(_, isMandatory) =>
                isMandatory ? "10px 24px 10px" : "8px"
              }
              footer={
                <>
                  {(isAdmin || isRequestView) && (
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: "#FFFFFF",
                          minHeight: "150px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            padding: "10px 24px 10px",
                            backgroundColor: "#B7BFCC",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "#111214",
                              fontWeight: 600,
                              fontSize: "20px",
                              fontFamily: "Roboto",
                            }}
                          >
                            {t("Settings")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            pl: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {ASSIGNABLE_SETTINGS_TABS.map((tab) => (
                            <FormControlLabel
                              key={tab.tabKey}
                              control={
                                <Checkbox
                                  checked={enabledSettingsTabs.includes(tab.tabKey)}
                                  onChange={() => handleSettingsTabToggle(tab.tabKey)}
                                  sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                                />
                              }
                              label={t(tab.labelKey)}
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontFamily: "Roboto",
                                  fontSize: "16px",
                                  color: enabledSettingsTabs.includes(tab.tabKey)
                                    ? "#111214"
                                    : "#666",
                                  fontWeight: enabledSettingsTabs.includes(tab.tabKey)
                                    ? 500
                                    : 400,
                                },
                              }}
                            />
                          ))}
                          {mapDetailsFeature && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(selectedFeatures[mapDetailsFeature.id])}
                                  onChange={() => handleAddOnChange(mapDetailsFeature.id)}
                                  sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                                />
                              }
                              label={t("Map details")}
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontFamily: "Roboto",
                                  fontSize: "16px",
                                  color: selectedFeatures[mapDetailsFeature.id]
                                    ? "#111214"
                                    : "#666",
                                  fontWeight: selectedFeatures[mapDetailsFeature.id]
                                    ? 500
                                    : 400,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  )}
                  {message && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#4CAF50",
                          fontWeight: 600,
                          fontSize: "18px",
                          fontFamily: "Roboto",
                          backgroundColor: "#E8F5E9",
                          padding: "12px 24px",
                          borderRadius: "8px",
                        }}
                      >
                        {message}
                      </Typography>
                    </Box>
                  )}
                  {renderActionButtons()}
                </>
              }
            />
          </Box>
        </Paper>
        <Box sx={{ height: "10vh" }}></Box>

        <RejectModal
          open={rejectModalOpen}
          onClose={() => setRejectModalOpen(false)}
          onConfirm={confirmReject}
          isActionLoading={isActionLoading}
          organizationName={orgNameToReject}
          rejectionReason={rejectionReason}
          setRejectionReason={setRejectionReason}
        />
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
      <Footer />
    </Box>
  );
}
